// src/pages/services/HealthInsurance.jsx
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaShieldAlt,
  FaUserFriends,
  FaClipboardCheck,
  FaHeartbeat,
  FaCheckCircle,
  FaQuestionCircle,
  FaCalculator,
  FaHospital,
} from "react-icons/fa";
import VisaDetailLayout from "../../components/VisaDetailLayout";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut", delay: i * 0.06 },
  }),
};

const OSHC_FEATURES = [
  "Meets Student Visa (Subclass 500) requirements",
  "Covers GP visits, hospital treatment & emergency",
  "Prescription medicines benefits (up to policy caps)",
  "Ambulance cover (insurer dependent)",
];

const OVHC_FEATURES = [
  "Meets visitor/temporary visa health requirements",
  "Covers GP & hospital (by level: Basic/Standard/Top)",
  "Custom length to match your stay",
  "Options for family/parents visitors",
];

const PROVIDERS = [
  { name: "ahm", url: "#", note: "Student & visitor options" },
  { name: "Bupa", url: "#", note: "Large hospital network" },
  { name: "nib", url: "#", note: "Flexible add-ons" },
  { name: "Medibank", url: "#", note: "24/7 health line" },
  { name: "Allianz Care", url: "#", note: "Strong student plans" },
];

export default function HealthInsurance() {
  const [tab, setTab] = useState("OSHC"); // "OSHC" | "OVHC"
  const [months, setMonths] = useState(12);
  const [coverLevel, setCoverLevel] = useState("standard");

  // very rough example rates (AUD/month) – we’ll just use this for the estimator UI
  const rate = useMemo(() => {
    if (tab === "OSHC") return 50; // typical student single from ~$45–$60
    // OVHC depends on level
    if (coverLevel === "basic") return 80;
    if (coverLevel === "top") return 150;
    return 110; // standard
  }, [tab, coverLevel]);

  const estimated = useMemo(() => Math.max(1, months) * rate, [months, rate]);

  return (
    <VisaDetailLayout title="Health Insurance (OSHC / OVHC)">
      {/* Decorative gradient blobs */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-80 w-80 rounded-full bg-blue-300/25 blur-3xl" />
        <div className="absolute -bottom-20 -right-24 h-96 w-96 rounded-full bg-indigo-300/25 blur-3xl" />
      </div>

      {/* Intro */}
      <motion.p
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="text-gray-700 text-lg mb-6"
      >
        Overseas Student Health Cover (OSHC) and Overseas Visitor Health Cover (OVHC)
        are mandatory for most international students and many temporary visa holders in
        Australia. We help you compare providers, choose the right level, and ensure your
        policy aligns with visa rules and dates.
      </motion.p>

      {/* Tabs */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <button
          onClick={() => setTab("OSHC")}
          className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
            tab === "OSHC"
              ? "bg-blue-600 text-white shadow"
              : "bg-white text-blue-700 border hover:bg-blue-50"
          }`}
        >
          OSHC (Students)
        </button>
        <button
          onClick={() => setTab("OVHC")}
          className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
            tab === "OVHC"
              ? "bg-indigo-600 text-white shadow"
              : "bg-white text-indigo-700 border hover:bg-indigo-50"
          }`}
        >
          OVHC (Visitors)
        </button>
      </div>

      {/* Why + Who + How  (animated, responsive grid) */}
      <div className="grid gap-6 md:grid-cols-3">
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="rounded-2xl border bg-white/70 p-6 shadow-sm backdrop-blur hover:-translate-y-1 hover:shadow-md transition"
        >
          <h2 className="flex items-center text-lg font-semibold text-blue-800 mb-2">
            <FaShieldAlt className="mr-2 text-blue-600" /> Why Health Insurance?
          </h2>
          <ul className="space-y-2 text-gray-700 text-sm">
            <li className="flex items-start gap-2">
              <FaCheckCircle className="mt-1 text-green-500" /> Meets visa health requirements
            </li>
            <li className="flex items-start gap-2">
              <FaCheckCircle className="mt-1 text-green-500" /> Covers GP visits & hospital treatment
            </li>
            <li className="flex items-start gap-2">
              <FaCheckCircle className="mt-1 text-green-500" /> Reduces unexpected medical costs
            </li>
          </ul>
        </motion.section>

        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="rounded-2xl border bg-white/70 p-6 shadow-sm backdrop-blur hover:-translate-y-1 hover:shadow-md transition"
        >
          <h2 className="flex items-center text-lg font-semibold text-blue-800 mb-2">
            <FaUserFriends className="mr-2 text-blue-600" /> Who Needs It?
          </h2>
          <ul className="space-y-2 text-gray-700 text-sm">
            <li className="flex items-start gap-2">
              <FaHeartbeat className="mt-1 text-rose-500" /> OSHC: Student Visa (Subclass 500)
            </li>
            <li className="flex items-start gap-2">
              <FaHospital className="mt-1 text-sky-500" /> OVHC: Visitors, parents & other temporary visas
            </li>
          </ul>
        </motion.section>

        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="rounded-2xl border bg-white/70 p-6 shadow-sm backdrop-blur hover:-translate-y-1 hover:shadow-md transition"
        >
          <h2 className="flex items-center text-lg font-semibold text-blue-800 mb-2">
            <FaClipboardCheck className="mr-2 text-blue-600" /> How We Assist
          </h2>
          <p className="text-gray-700 text-sm">
            We compare plans, align policy dates to your COE/visa, and guide claims & provider
            contact. You get compliant cover at a fair price—without guesswork.
          </p>
        </motion.section>
      </div>

      {/* Feature list per tab */}
      <AnimatePresence mode="wait">
        <motion.section
          key={tab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.35 }}
          className="mt-6 rounded-2xl border bg-gradient-to-b from-blue-50/70 to-white p-6"
        >
          <h3 className="text-blue-800 font-semibold mb-3">
            {tab === "OSHC" ? "OSHC Highlights" : "OVHC Highlights"}
          </h3>
          <ul className="grid sm:grid-cols-2 gap-2 text-sm text-gray-700">
            {(tab === "OSHC" ? OSHC_FEATURES : OVHC_FEATURES).map((f) => (
              <li key={f} className="flex items-start gap-2">
                <FaCheckCircle className="mt-1 text-emerald-500" /> {f}
              </li>
            ))}
          </ul>
        </motion.section>
      </AnimatePresence>

      {/* Provider grid */}
      <section className="mt-8">
        <motion.h3
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-blue-800 font-semibold mb-3"
        >
          Popular Providers
        </motion.h3>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {PROVIDERS.map((p, i) => (
            <motion.a
              key={p.name}
              href={p.url}
              onClick={(e) => e.preventDefault()}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={i}
              className="group rounded-xl border bg-white/70 p-4 text-center shadow-sm backdrop-blur transition hover:-translate-y-1 hover:shadow-md"
            >
              <div className="text-lg font-semibold text-blue-900">{p.name}</div>
              <div className="mt-1 text-xs text-gray-600">{p.note}</div>
              <span className="mt-3 inline-block text-xs font-medium text-blue-700 group-hover:underline">
                Compare →
              </span>
            </motion.a>
          ))}
        </div>
      </section>

      {/* Mini cost estimator */}
      <section className="mt-10">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="rounded-2xl border bg-white/80 p-6 shadow-sm backdrop-blur"
        >
          <div className="flex items-center gap-2 text-blue-800 font-semibold mb-4">
            <FaCalculator /> Quick Cost Estimator
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="flex flex-col">
              <label className="text-sm text-gray-700 mb-1">Cover Type</label>
              <select
                className="rounded-lg border px-3 py-2"
                value={tab}
                onChange={(e) => setTab(e.target.value)}
              >
                <option value="OSHC">OSHC (Students)</option>
                <option value="OVHC">OVHC (Visitors)</option>
              </select>
            </div>

            {tab === "OVHC" && (
              <div className="flex flex-col">
                <label className="text-sm text-gray-700 mb-1">OVHC Level</label>
                <select
                  className="rounded-lg border px-3 py-2"
                  value={coverLevel}
                  onChange={(e) => setCoverLevel(e.target.value)}
                >
                  <option value="basic">Basic</option>
                  <option value="standard">Standard</option>
                  <option value="top">Top</option>
                </select>
              </div>
            )}

            <div className="flex flex-col">
              <label className="text-sm text-gray-700 mb-1">Months</label>
              <input
                type="number"
                min={1}
                max={60}
                className="rounded-lg border px-3 py-2"
                value={months}
                onChange={(e) => setMonths(parseInt(e.target.value || "1", 10))}
              />
            </div>
          </div>

          <div className="mt-4 rounded-lg bg-blue-50 px-4 py-3 text-blue-800">
            Estimated total: <span className="font-bold">${estimated.toFixed(0)}</span> AUD
            <span className="ml-2 text-sm text-blue-700/70">
              (illustrative only — we’ll confirm exact provider pricing)
            </span>
          </div>
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="mt-10">
        <motion.h3
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-blue-800 font-semibold mb-3"
        >
          FAQs
        </motion.h3>

        <div className="space-y-3">
          {[
            {
              q: "When should I start my OSHC?",
              a: "Usually from the course start date (COE) or a little earlier when you arrive. Your visa typically needs the policy to cover the full study period.",
            },
            {
              q: "Can I switch providers later?",
              a: "Yes. Many students change providers on renewal to save costs or upgrade benefits. We’ll help with the timing and paperwork.",
            },
            {
              q: "Does OVHC cover emergencies?",
              a: "Depending on level (Basic/Standard/Top), OVHC can include emergency and hospital cover. We’ll guide you to an appropriate level.",
            },
          ].map((item, i) => (
            <Accordion key={i} title={item.q} body={item.a} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mt-12">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 p-[1px]"
        >
          <div className="rounded-2xl bg-white/90 px-6 py-8 backdrop-blur">
            <div className="grid items-center gap-6 md:grid-cols-2">
              <div>
                <h4 className="text-2xl font-bold text-blue-900">
                  Get the right cover at the right price.
                </h4>
                <p className="mt-1 text-gray-600">
                  We’ll compare providers and align your policy with your visa dates.
                </p>
              </div>
              <div className="flex md:justify-end">
                <Link
                  to="/contact"
                  className="inline-flex items-center rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white shadow hover:bg-blue-500"
                >
                  Talk to a consultant
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </section>
    </VisaDetailLayout>
  );
}

/* --- small accordion component --- */
function Accordion({ title, body }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border bg-white/80 p-4 shadow-sm backdrop-blur">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="font-medium text-blue-900 flex items-center gap-2">
          <FaQuestionCircle className="text-blue-600" /> {title}
        </span>
        <span className="text-blue-700">{open ? "–" : "+"}</span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.p
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="mt-2 text-gray-700 text-sm leading-relaxed"
          >
            {body}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}
